import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, MenuItem, Alert, CircularProgress,
    Box, Typography, Divider,
} from "@mui/material";
import { assetService } from "../../services/assetService";
import { Asset, AssetCreate, AssetUpdate, AssetCategory } from "../../types/assets";
import { useAssetCategories } from "../../hooks/useAssetCategories";
import { AccountPicker } from "../accounts/AccountPicker";
import { PaymentMethod } from "../../types";
import { ASSET_PAYMENT_METHODS, PAYMENT_METHOD_LABELS } from "../../utils/paymentMethods";

interface AssetFormProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    asset?: Asset | null;
    mode: "create" | "edit";
}

const LIFE_PRESETS = [
    { months: 12,  label: "1 year" },
    { months: 24,  label: "2 years" },
    { months: 36,  label: "3 years" },
    { months: 48,  label: "4 years" },
    { months: 60,  label: "5 years" },
    { months: 84,  label: "7 years" },
    { months: 120, label: "10 years" },
];

const today = () => new Date().toISOString().split("T")[0];

export const AssetForm: React.FC<AssetFormProps> = ({ open, onClose, onSuccess, asset, mode }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const { categories, getCategoryMeta } = useAssetCategories();

    const [form, setForm] = useState<{
        name: string;
        category: AssetCategory;
        description: string;
        purchase_date: string;
        purchase_cost: string;
        payment_method: PaymentMethod;
        account_id: string | null;
        salvage_value: string;
        useful_life_months: string;
        vendor_name: string;
        serial_number: string;
        status: string;
        disposal_date: string;
        disposal_value: string;
    }>({
        name: "",
        category: "EQUIPMENT",
        description: "",
        purchase_date: today(),
        purchase_cost: "",
        payment_method: "CASH",
        account_id: null,
        salvage_value: "0",
        useful_life_months: "60",
        vendor_name: "",
        serial_number: "",
        // edit-only
        status: "ACTIVE",
        disposal_date: "",
        disposal_value: "",
    });

    useEffect(() => {
        if (!open) return;
        setError("");
        if (mode === "edit" && asset) {
            setForm({
                name: asset.name,
                category: asset.category,
                description: asset.description || "",
                purchase_date: asset.purchase_date,
                purchase_cost: String(asset.purchase_cost),
                payment_method: asset.payment_method || "CASH",
                account_id: asset.account_id || null,
                salvage_value: String(asset.salvage_value),
                useful_life_months: String(asset.useful_life_months),
                vendor_name: asset.vendor_name || "",
                serial_number: asset.serial_number || "",
                status: asset.status,
                disposal_date: asset.disposal_date || "",
                disposal_value: asset.disposal_value != null ? String(asset.disposal_value) : "",
            });
        } else {
            setForm({
                name: "", category: "EQUIPMENT", description: "",
                purchase_date: today(), purchase_cost: "", payment_method: "CASH",
                account_id: null,
                salvage_value: "0",
                useful_life_months: "60", vendor_name: "", serial_number: "",
                status: "ACTIVE", disposal_date: "", disposal_value: "",
            });
        }
    }, [open, mode, asset]);

    const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    const handleCategoryChange = (cat: AssetCategory) => {
        setForm((f) => ({
            ...f,
            category: cat,
            useful_life_months: String(getCategoryMeta(cat).default_useful_life_months),
        }));
    };

    const monthlyPreview = () => {
        const cost = parseFloat(form.purchase_cost) || 0;
        const salvage = parseFloat(form.salvage_value) || 0;
        const life = parseInt(form.useful_life_months) || 1;
        if (cost <= 0) return null;
        const monthly = (cost - salvage) / life;
        return monthly.toFixed(2);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        const cost = parseFloat(form.purchase_cost);
        const salvage = parseFloat(form.salvage_value) || 0;
        const life = parseInt(form.useful_life_months);

        if (!form.name.trim()) { setError("Asset name is required"); return; }
        if (!cost || cost <= 0) { setError("Purchase cost must be greater than 0"); return; }
        if (salvage < 0) { setError("Salvage value cannot be negative"); return; }
        if (salvage >= cost) { setError("Salvage value must be less than purchase cost"); return; }
        if (!life || life <= 0) { setError("Useful life must be at least 1 month"); return; }

        try {
            setLoading(true);
            if (mode === "create") {
                const data: AssetCreate = {
                    name: form.name.trim(),
                    category: form.category,
                    description: form.description || undefined,
                    purchase_date: form.purchase_date,
                    purchase_cost: cost,
                    payment_method: form.payment_method,
                    account_id: form.account_id,
                    salvage_value: salvage,
                    useful_life_months: life,
                    vendor_name: form.vendor_name || undefined,
                    serial_number: form.serial_number || undefined,
                };
                await assetService.createAsset(data);
            } else if (asset) {
                const data: AssetUpdate = {
                    name: form.name.trim(),
                    category: form.category,
                    description: form.description || undefined,
                    vendor_name: form.vendor_name || undefined,
                    serial_number: form.serial_number || undefined,
                    payment_method: form.payment_method,
                    // null (not undefined) so editing can explicitly clear a
                    // previously-tagged account — see AccountPicker's docstring.
                    account_id: form.account_id,
                    status: form.status as any,
                    disposal_date: form.disposal_date || undefined,
                    disposal_value: form.disposal_value ? parseFloat(form.disposal_value) : undefined,
                };
                await assetService.updateAsset(asset.asset_id, data);
            }
            onSuccess();
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || "Failed to save asset");
        } finally {
            setLoading(false);
        }
    };

    const catMeta = getCategoryMeta(form.category);
    const preview = monthlyPreview();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    {mode === "create" ? "Add New Asset" : "Edit Asset"}
                </DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                        {/* Name */}
                        <TextField
                            label="Asset Name"
                            value={form.name}
                            onChange={set("name")}
                            required fullWidth
                            placeholder="e.g. Shop Air Conditioner, HP Laptop, Delivery Bike"
                        />

                        {/* Category */}
                        <TextField
                            select label="Category" value={form.category} fullWidth required
                            onChange={(e) => handleCategoryChange(e.target.value as AssetCategory)}
                            helperText={catMeta.life_hint}
                        >
                            {categories.map((c) => (
                                <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
                            ))}
                        </TextField>

                        <Divider />

                        {/* Purchase date + cost side by side */}
                        <Box sx={{ display: "flex", gap: 2 }}>
                            <TextField
                                type="date" label="Purchase Date" value={form.purchase_date}
                                onChange={set("purchase_date")} required fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                type="number" label="Purchase Cost (৳)" value={form.purchase_cost}
                                onChange={set("purchase_cost")} required fullWidth
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                        </Box>

                        <TextField
                            select label="Payment Method" value={form.payment_method}
                            onChange={set("payment_method")} fullWidth required
                            helperText="How this purchase was actually paid for"
                        >
                            {ASSET_PAYMENT_METHODS.map((m) => (
                                <MenuItem key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</MenuItem>
                            ))}
                        </TextField>

                        <AccountPicker
                            value={form.account_id}
                            onChange={(account_id) => setForm((f) => ({ ...f, account_id }))}
                            helperText="Which cash/bank account this purchase came out of"
                        />

                        {/* Salvage value + useful life */}
                        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                            <TextField
                                type="number" label="Salvage Value (৳)" value={form.salvage_value}
                                onChange={set("salvage_value")} fullWidth
                                inputProps={{ min: 0, step: 0.01 }}
                                helperText="Expected value when you sell/discard it. Enter 0 if unsure."
                            />
                            <TextField
                                select label="Useful Life" value={form.useful_life_months}
                                onChange={set("useful_life_months")} fullWidth required
                                helperText="How long this asset will be used"
                            >
                                {LIFE_PRESETS.map((p) => (
                                    <MenuItem key={p.months} value={String(p.months)}>
                                        {p.label} ({p.months} months)
                                    </MenuItem>
                                ))}
                                <MenuItem value="custom">Custom…</MenuItem>
                            </TextField>
                        </Box>

                        {form.useful_life_months === "custom" && (
                            <TextField
                                type="number" label="Custom Useful Life (months)"
                                value="" onChange={set("useful_life_months")} fullWidth required
                                inputProps={{ min: 1 }}
                            />
                        )}

                        {/* Monthly depreciation preview */}
                        {preview && (
                            <Box sx={{ bgcolor: "primary.50", border: "1px solid", borderColor: "primary.200", borderRadius: 1, p: 1.5 }}>
                                <Typography variant="body2" color="primary.main" fontWeight={600}>
                                    Monthly depreciation: ৳{parseFloat(preview).toLocaleString("en-BD", { minimumFractionDigits: 2 })}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    This amount will be charged as a depreciation expense each month in the P&L report.
                                </Typography>
                            </Box>
                        )}

                        <Divider />

                        {/* Optional fields */}
                        <Box sx={{ display: "flex", gap: 2 }}>
                            <TextField
                                label="Vendor / Supplier" value={form.vendor_name}
                                onChange={set("vendor_name")} fullWidth
                                placeholder="Where did you buy this?"
                            />
                            <TextField
                                label="Serial / Model No." value={form.serial_number}
                                onChange={set("serial_number")} fullWidth
                            />
                        </Box>

                        <TextField
                            label="Notes" value={form.description}
                            onChange={set("description")} fullWidth multiline rows={2}
                        />

                        {/* Disposal fields — edit only */}
                        {mode === "edit" && (
                            <>
                                <Divider />
                                <TextField
                                    select label="Status" value={form.status}
                                    onChange={set("status")} fullWidth
                                >
                                    <MenuItem value="ACTIVE">Active</MenuItem>
                                    <MenuItem value="DISPOSED">Disposed / Sold</MenuItem>
                                </TextField>

                                {form.status === "DISPOSED" && (
                                    <Box sx={{ display: "flex", gap: 2 }}>
                                        <TextField
                                            type="date" label="Disposal Date" value={form.disposal_date}
                                            onChange={set("disposal_date")} fullWidth
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        <TextField
                                            type="number" label="Sale / Disposal Value (৳)"
                                            value={form.disposal_value} onChange={set("disposal_value")} fullWidth
                                            inputProps={{ min: 0, step: 0.01 }}
                                            helperText="What you received when selling/disposing"
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={22} /> : mode === "create" ? "Add Asset" : "Save Changes"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
